import type {
  ParseActionArgsResult,
  SofiaProposedAction,
  UpdateTestCaseActionArgs,
} from "../model/types"
import {
  isRecord,
  MAX_UPDATE_TEST_CASES,
  readBoolean,
  readInputType,
  readJsonObject,
  readNumber,
  readScriptTurns,
  readString,
  readStringAllowEmpty,
  readStringArray,
  TEST_CASE_CLEAR_SENTINEL,
} from "./actionArgumentReaders"
import { readPersonaConfiguration } from "./personaActionConfig"

/** One card row per attribute an update_test_case patch sets or removes. */
export function summarizeAttributePatch(
  value: unknown,
): Array<{ key: string; value: string }> {
  if (!isRecord(value)) {
    return []
  }
  return Object.entries(value).map(([name, attribute]) => ({
    key: `attribute ${name}`,
    value:
      attribute === null
        ? "(remove)"
        : typeof attribute === "string"
          ? attribute
          : JSON.stringify(attribute),
  }))
}

/**
 * Narrow one sparse test-case patch: the args of `update_test_case`, or one
 * entry of an `update_test_cases` batch (then `prefix` names the entry in each
 * problem). A field sent as JSON null is an omission, exactly as sofia's
 * validator treats it (never a clear: an empty string clears a description).
 * Pushes the problems and returns null when the patch is unusable — a patch
 * that changes nothing is a problem except redundant batch attributes.
 */
function readTestCasePatch(
  rawArgs: Record<string, unknown>,
  problems: string[],
  prefix = "",
  skipEmptyAttributes = false,
): UpdateTestCaseActionArgs | null {
  const before = problems.length
  const args = Object.fromEntries(
    Object.entries(rawArgs).filter(([, value]) => value !== null),
  )
  const test_case_id = readString(args, "test_case_id")
  if (!test_case_id) {
    problems.push(
      prefix ? `${prefix}test_case_id is missing` : "missing test_case_id",
    )
    return null
  }
  const input_str = readString(args, "input_str")
  // Batch entries only: the text to add after the case's current input. Read
  // untrimmed at the start, since a leading newline asks for a new paragraph.
  const rawAppend = prefix ? args.append_input_str : undefined
  const append_input_str =
    typeof rawAppend === "string" && rawAppend.trim().length > 0
      ? rawAppend.trimEnd()
      : undefined
  if (rawAppend !== undefined && append_input_str === undefined) {
    problems.push(`${prefix}append_input_str must be non-empty text`)
  }
  if (append_input_str !== undefined && input_str !== undefined) {
    problems.push(`${prefix}sets both input_str and append_input_str`)
  }
  const description = readStringAllowEmpty(args, "description")
  // The editor turns its clear sentinel into "" for both text fields, so a
  // literal sentinel in either would be staged as a blank value.
  for (const [field, value] of [
    ["input_str", input_str],
    ["append_input_str", append_input_str?.trim()],
    ["description", description],
  ] as const) {
    if (value === TEST_CASE_CLEAR_SENTINEL) {
      problems.push(
        `${prefix}${field} must not be the reserved value ${TEST_CASE_CLEAR_SENTINEL}`,
      )
    }
  }
  const expected_behaviors = readStringArray(args, "expected_behaviors")
  const attributePatch = readJsonObject(args, "expected_output_json")
  if (args.expected_output_json != null && attributePatch === undefined) {
    problems.push(`${prefix}expected_output_json must be a JSON object`)
  }
  // An empty merge patch changes no attributes and cannot make an
  // otherwise empty proposal valid for confirmation (single or batch).
  const expected_output_json =
    attributePatch && Object.keys(attributePatch).length > 0
      ? attributePatch
      : undefined
  // Partial patch: no SCRIPT/turns pairing here — the backend validates the
  // final stored state.
  const input_type =
    args.input_type == null ? undefined : readInputType(args, problems, prefix)
  const script_turns =
    args.script_turns == null
      ? undefined
      : readScriptTurns(args, "script_turns", problems, prefix)
  // An append extends the case's current input as it stands. Changing the
  // case's type in the same entry changes what that input is (a script's is
  // empty, its exact turns aside), so the appended text would be misplaced.
  if (
    append_input_str !== undefined &&
    (input_type !== undefined || script_turns !== undefined)
  ) {
    problems.push(
      `${prefix}append_input_str cannot be combined with an input_type change or script_turns`,
    )
  }
  if (problems.length > before) {
    return null
  }
  if (
    input_str === undefined &&
    append_input_str === undefined &&
    description === undefined &&
    expected_behaviors === undefined &&
    expected_output_json === undefined &&
    input_type === undefined &&
    script_turns === undefined
  ) {
    // A redundant attribute entry may accompany real sibling edits. Keep
    // validating the batch's IDs and shapes, but don't stage this no-op.
    if (
      skipEmptyAttributes &&
      (rawArgs.expected_output_json === null || attributePatch !== undefined)
    ) {
      return null
    }
    problems.push(
      prefix ? `${prefix}changes nothing` : "missing fields to update",
    )
    return null
  }
  return {
    test_case_id,
    input_str,
    append_input_str,
    description,
    expected_behaviors,
    expected_output_json,
    input_type,
    script_turns,
  }
}

export function parseModifyActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only resource mutation actions.
  switch (action.kind) {
    case "update_agent": {
      for (const field of ["prompt", "language"]) {
        if (
          action.args[field] !== undefined &&
          action.args[field] !== null &&
          typeof action.args[field] !== "string"
        ) {
          return { ok: false, problems: [`${field} must be text or null`] }
        }
      }
      const agent_id = readString(action.args, "agent_id")
      if (!agent_id) {
        return { ok: false, problems: ["missing agent_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_agent",
          args: {
            agent_id,
            display_name: readString(action.args, "display_name"),
            phone_number: readString(action.args, "phone_number"),
            ...(Object.hasOwn(action.args, "prompt")
              ? { prompt: action.args.prompt as string | null }
              : {}),
            ...(Object.hasOwn(action.args, "language")
              ? { language: action.args.language as string | null }
              : {}),
          },
        },
      }
    }
    case "delete_agent": {
      const agent_id = readString(action.args, "agent_id")
      if (!agent_id) {
        return { ok: false, problems: ["missing agent_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_agent", args: { agent_id } },
      }
    }
    case "update_persona": {
      const persona_id = readString(action.args, "persona_id")
      if (!persona_id) {
        return { ok: false, problems: ["missing persona_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_persona",
          args: {
            persona_id,
            name: readString(action.args, "name"),
            persona_prompt: readStringAllowEmpty(action.args, "persona_prompt"),
            voice_name: readString(action.args, "voice_name"),
            language_code: readString(action.args, "language_code"),
            ...readPersonaConfiguration(action.args),
          },
        },
      }
    }
    case "delete_persona": {
      const persona_id = readString(action.args, "persona_id")
      if (!persona_id) {
        return { ok: false, problems: ["missing persona_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_persona", args: { persona_id } },
      }
    }
    case "update_metric": {
      const metric_id = readString(action.args, "metric_id")
      if (!metric_id) {
        return { ok: false, problems: ["missing metric_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_metric",
          args: {
            metric_id,
            metric_name: readString(action.args, "metric_name"),
            description: readString(action.args, "description"),
            prompt: readString(action.args, "prompt"),
            span_name: readString(action.args, "span_name"),
            metric_attribute: readString(action.args, "metric_attribute"),
            aggregation_method: readString(action.args, "aggregation_method"),
            value_source: readString(action.args, "value_source"),
            unit: readString(action.args, "unit"),
          },
        },
      }
    }
    case "delete_metric": {
      const metric_id = readString(action.args, "metric_id")
      if (!metric_id) {
        return { ok: false, problems: ["missing metric_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_metric", args: { metric_id } },
      }
    }
    case "update_dashboard": {
      const dashboard_id = readString(action.args, "dashboard_id")
      if (!dashboard_id) {
        return { ok: false, problems: ["missing dashboard_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_dashboard",
          args: {
            dashboard_id,
            display_name: readString(action.args, "display_name"),
            description: readString(action.args, "description"),
            is_favorite: readBoolean(action.args, "is_favorite"),
            is_default: readBoolean(action.args, "is_default"),
          },
        },
      }
    }
    case "delete_dashboard": {
      const dashboard_id = readString(action.args, "dashboard_id")
      if (!dashboard_id) {
        return { ok: false, problems: ["missing dashboard_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_dashboard", args: { dashboard_id } },
      }
    }
    case "update_report": {
      const report_id = readString(action.args, "report_id")
      if (!report_id) {
        return { ok: false, problems: ["missing report_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_report",
          args: {
            report_id,
            name: readString(action.args, "name"),
            run_ids: readStringArray(action.args, "run_ids"),
            compare_by: readString(action.args, "compare_by"),
            metadata_key: readString(action.args, "metadata_key"),
            permissions: readString(action.args, "permissions"),
          },
        },
      }
    }
    case "delete_report": {
      const report_id = readString(action.args, "report_id")
      if (!report_id) {
        return { ok: false, problems: ["missing report_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_report", args: { report_id } },
      }
    }
    case "update_monitor": {
      const monitor_id = readString(action.args, "monitor_id")
      if (!monitor_id) {
        return { ok: false, problems: ["missing monitor_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_monitor",
          args: {
            monitor_id,
            name: readString(action.args, "name"),
            threshold_float: readNumber(action.args, "threshold_float"),
            operator: readString(action.args, "operator"),
            scope: readString(action.args, "scope"),
            cooldown_seconds: readNumber(action.args, "cooldown_seconds"),
          },
        },
      }
    }
    case "delete_monitor": {
      const monitor_id = readString(action.args, "monitor_id")
      if (!monitor_id) {
        return { ok: false, problems: ["missing monitor_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_monitor", args: { monitor_id } },
      }
    }
    case "update_test_set": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_test_set",
          args: {
            test_set_id,
            display_name: readString(action.args, "display_name"),
            description: readString(action.args, "description"),
            test_set_type: readString(action.args, "test_set_type"),
            tags: readStringArray(action.args, "tags"),
          },
        },
      }
    }
    case "update_test_case": {
      const problems: string[] = []
      const patch = readTestCasePatch(action.args, problems)
      if (!patch) {
        return { ok: false, problems }
      }
      return { ok: true, action: { kind: "update_test_case", args: patch } }
    }
    case "update_test_cases": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      const raw = action.args.changes
      if (
        !Array.isArray(raw) ||
        raw.length === 0 ||
        raw.length > MAX_UPDATE_TEST_CASES
      ) {
        return {
          ok: false,
          problems: [
            `changes must contain 1-${MAX_UPDATE_TEST_CASES} test-case changes`,
          ],
        }
      }
      const problems: string[] = []
      const changes: UpdateTestCaseActionArgs[] = []
      const seen = new Set<string>()
      for (const [index, entry] of raw.entries()) {
        if (!isRecord(entry)) {
          problems.push(`changes[${index}] must be an object`)
          continue
        }
        const testCaseId = readString(entry, "test_case_id")
        if (testCaseId && seen.has(testCaseId)) {
          problems.push(`changes[${index}] repeats test_case_id ${testCaseId}`)
          continue
        }
        if (testCaseId) {
          seen.add(testCaseId)
        }
        const change = readTestCasePatch(
          entry,
          problems,
          `changes[${index}].`,
          true,
        )
        if (change) {
          changes.push(change)
        }
      }
      if (problems.length > 0) {
        return { ok: false, problems }
      }
      if (changes.length === 0) {
        return {
          ok: false,
          problems: ["changes must contain at least one update"],
        }
      }
      return {
        ok: true,
        action: { kind: "update_test_cases", args: { test_set_id, changes } },
      }
    }
    case "delete_test_set": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_test_set", args: { test_set_id } },
      }
    }
    case "delete_test_case": {
      const test_case_id = readString(action.args, "test_case_id")
      if (!test_case_id) {
        return { ok: false, problems: ["missing test_case_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_test_case", args: { test_case_id } },
      }
    }
    default:
      return undefined
  }
}
